import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { logout, getUserData, storeUserData } from "../../../services/apiService";
import {
  acceptTermsAndConditions,
  parseCredentialStatus,
} from "../../../services/credentialPolicyService";
import {
  mergeCredentialStatusIntoUser,
  resolvePostLoginRoute,
  storeCredentialGateFromUser,
} from "../../../utils/credentialGateUtils";
import "./ChangeCredentialsModal.scss";
import "./TermsAndConditionsAccept.scss";

const SCROLL_END_THRESHOLD_PX = 32;

const TermsAndConditionsAccept = () => {
  const navigate = useNavigate();
  const scrollParentRef = useRef(null);
  const iframeRef = useRef(null);
  const [iframeHeight, setIframeHeight] = useState(800);
  const [checked, setChecked] = useState(false);
  const [hasScrolledToEnd, setHasScrolledToEnd] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const userData = getUserData();

  const checkParentScroll = useCallback(() => {
    const el = scrollParentRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight <= clientHeight + SCROLL_END_THRESHOLD_PX) {
      setHasScrolledToEnd(true);
      return;
    }

    if (scrollTop + clientHeight >= scrollHeight - SCROLL_END_THRESHOLD_PX) {
      setHasScrolledToEnd(true);
    }
  }, []);

  const resizeIframeToContent = useCallback(() => {
    const iframe = iframeRef.current;
    const doc = iframe?.contentDocument;
    if (!iframe || !doc) return;

    const root = doc.documentElement;
    const body = doc.body;
    if (body) {
      body.style.overflow = "hidden";
      body.style.margin = "0";
    }
    if (root) {
      root.style.overflow = "hidden";
    }

    const height = Math.max(
      body?.scrollHeight || 0,
      root?.scrollHeight || 0,
      body?.offsetHeight || 0,
      root?.offsetHeight || 0,
    );
    if (height > 0) {
      setIframeHeight(height);
    }
  }, []);

  const handleIframeLoad = useCallback(() => {
    resizeIframeToContent();
    // Re-measure after fonts/layout settle
    window.setTimeout(resizeIframeToContent, 100);
    window.setTimeout(resizeIframeToContent, 400);
    window.setTimeout(checkParentScroll, 450);
  }, [resizeIframeToContent, checkParentScroll]);

  useEffect(() => {
    const el = scrollParentRef.current;
    if (!el) return undefined;

    el.addEventListener("scroll", checkParentScroll, { passive: true });
    window.addEventListener("resize", resizeIframeToContent);
    checkParentScroll();

    return () => {
      el.removeEventListener("scroll", checkParentScroll);
      window.removeEventListener("resize", resizeIframeToContent);
    };
  }, [checkParentScroll, resizeIframeToContent]);

  useEffect(() => {
    checkParentScroll();
  }, [iframeHeight, checkParentScroll]);

  const handleCancel = () => {
    logout(navigate);
  };

  const handleAccept = async () => {
    if (!hasScrolledToEnd) {
      setError("Please scroll to the end of the terms before accepting.");
      return;
    }
    if (!checked) {
      setError("Please check the box to agree to the terms and conditions.");
      return;
    }
    if (!userData?.StaffId || !userData?.EmailId) {
      setError("Session expired. Please sign in again.");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await acceptTermsAndConditions({
        staffId: userData.StaffId,
        emailId: userData.EmailId,
        organizationId: userData.OrganizationId,
      });
      if (response.success !== 1) {
        setError(response.message || "Could not save acceptance.");
        return;
      }

      const status = parseCredentialStatus(response);
      const merged = mergeCredentialStatusIntoUser(
        { ...userData, AccountStatus: "1" },
        status,
      );
      storeUserData(merged);
      storeCredentialGateFromUser(merged);

      const hasMultipleOrgs =
        Array.isArray(merged.OtherOrgLink) && merged.OtherOrgLink.length > 1;
      navigate(resolvePostLoginRoute(merged, hasMultipleOrgs), { replace: true });
    } catch (err) {
      setError(err?.message || "Could not save acceptance.");
    } finally {
      setLoading(false);
    }
  };

  const canAccept = hasScrolledToEnd && checked && !loading;

  return (
    <div
      ref={scrollParentRef}
      className="tmc-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Terms and Conditions"
    >
      <div className="tmc-shell">
        <div className="tmc-body">
          <iframe
            ref={iframeRef}
            title="Terms and Conditions"
            className="tmc-eula-frame"
            src={`${process.env.PUBLIC_URL || ""}/terms-eula.html`}
            onLoad={handleIframeLoad}
            scrolling="no"
            style={{ height: iframeHeight }}
          />
        </div>

        <div className="tmc-footer-bar">
          <div className="tmc-footer">
            <div className="tmc-footer-start">
              {error && (
                <div className="alert alert-danger py-1 px-2 small mb-0" role="alert">
                  {error}
                </div>
              )}
              {!hasScrolledToEnd && (
                <p className="tmc-scroll-hint mb-0">
                  Scroll to the end of the document to enable Accept.
                </p>
              )}
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="tmc-agree"
                  checked={checked}
                  disabled={!hasScrolledToEnd}
                  onChange={(e) => setChecked(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="tmc-agree">
                  I agree to the terms and conditions.
                </label>
              </div>
            </div>
            <div className="tmc-footer-actions">
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={handleAccept}
                disabled={!canAccept}
              >
                {loading ? "Saving…" : "Accept"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditionsAccept;
